import {useToast} from '@chakra-ui/react';
import {
  useDeleteNotificationMessageByIdMutation,
  useGetGameByIdQuery,
  useQueueNotificationMessageMutation,
  useSendPushNotificationMessageMutation,
} from 'app/api';
import {TOAST_DURATION_MEDIUM} from '../playoffSchedulingConstants';
import {GameView} from '../../../../types';

interface UseGameNotificationReturn {
  sendGameNotification: (eventId: number, game: GameView) => Promise<void>;
  isLoading: boolean;
}

export const useGameNotification = (): UseGameNotificationReturn => {
  const toast = useToast();
  const [queueNotificationMessage, {isLoading: isQueueing}] = useQueueNotificationMessageMutation();
  const [sendPushNotificationMessage, {isLoading: isSending}] = useSendPushNotificationMessageMutation();
  const [deletePushNotificationMessage] = useDeleteNotificationMessageByIdMutation();

  const sendGameNotification = async (
    eventId: number,
    gameView: GameView
  ): Promise<void> => {

    try {
      const {id, homeTeamName, awayTeamName, courtName, playoff} = gameView;
      const title = `${playoff ? 'Playoff' : 'Game'} Scheduled: ${homeTeamName} vs ${awayTeamName}`;
      const body = `Your playoff game has been scheduled on ${courtName}. Check your schedule for details.`;

      const queuedNotification = await queueNotificationMessage({
        queueNotification: {
          title,
          body,
          subscriptionType: 'GAME',
          eventId,
          resourceId: id,
        },
      }).unwrap();

      await sendPushNotificationMessage(queuedNotification.id).unwrap();
      await deletePushNotificationMessage(queuedNotification.id).unwrap();

      toast({
        title: 'Players Notified',
        description: 'A push notification was sent to the players for this game.',
        status: 'success',
        duration: TOAST_DURATION_MEDIUM,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: 'Error Sending Notification',
        description: error.data?.message || 'An error occurred while notifying players.',
        status: 'error',
        duration: TOAST_DURATION_MEDIUM,
        isClosable: true,
      });
    }
  };

  return {
    sendGameNotification,
    isLoading: isQueueing || isSending,
  };
};
